/**
 * The comparison pages, the guides and the about page, as plain data: Article.tsx renders them, and the server writes the
 * same words into llms-full.txt and answers a request for Markdown with them (site.ts). Written for answer engines as
 * much as for people (docs/aeo-playbook.md): every section's heading is a question a buyer asks, its first sentence
 * answers it on its own and names RareMargin, and each page shows the date it last changed.
 *
 * Facts about RareMargin must match the code; facts about other products come only from their own websites, listed in
 * the page's sources with the date they were read (`checked`). Re-read those pages before changing `updated`, and take
 * out anything that no longer holds. Plain TypeScript with no React, like content.ts.
 */
import type { Facts, PagePath } from './content';

export const COMPARE_PATHS = ['/compare/raremargin-vs-rillet', '/compare/raremargin-vs-quickbooks-online', '/compare/raremargin-vs-netsuite'] as const;
export const GUIDE_PATHS = [
  '/docs/asc-606-revenue-recognition',
  '/docs/how-continuous-close-works',
  '/docs/ai-journal-entries-and-approvals',
  '/docs/api-mcp-server-and-webhooks',
  '/docs/use-raremargin-in-claude-chatgpt-and-cursor',
  '/docs/set-up-raremargin-and-move-your-books',
] as const;
export type ArticlePath = (typeof COMPARE_PATHS)[number] | (typeof GUIDE_PATHS)[number] | '/about';

/** A paragraph, a list, a table (the first column reads as row headings), or code. */
export type Block = string | { list: string[] } | { table: Table } | { code: string };
export interface Table {
  head?: string[];
  rows: string[][];
}

export interface Section {
  id: string;
  /** The heading, as the question people ask. */
  q: string;
  /** The answer: one or two sentences that still make sense when quoted alone. */
  a: string;
  more?: Block[];
}

export interface Article {
  eyebrow: string;
  h1: string;
  /** What the page answers, in two or three sentences, first under the h1. */
  lead: string;
  facts?: Table;
  sections: Section[];
  sources?: { label: string; url: string }[];
  related: PagePath[];
}

export interface ArticleMeta {
  title: string;
  description: (f: Facts, price: string | null) => string;
  name: string;
  kind: 'compare' | 'guide' | 'about';
  published: string;
  updated: string;
  /** Other companies the page is about, for the structured data. */
  mentions?: { name: string; url: string }[];
}

/** When the competitors' websites were last read for the comparison pages. */
export const CHECKED = '2026-11-03';
const UPDATED = '2026-11-03';

/** "3 November 2026", like the legal pages. */
export const longDate = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * Who wrote a comparison page and how, shown under its lead. Self-promoting comparisons that hide both were the pattern
 * behind Google's January 2026 drops (docs/aeo-playbook.md), and readers deserve to know anyway.
 */
export function compareNote(path: ArticlePath) {
  const meta = ARTICLE_META[path];
  const other = meta.kind === 'compare' ? meta.mentions?.[0]?.name : undefined;
  if (!other) return null;
  return `RareMargin wrote this comparison. Facts about ${other} come only from ${other}’s own website, read on ${longDate(CHECKED)} and listed under Sources; facts about RareMargin come from the product itself. Where ${other} is the better choice, the page says so.`;
}

const per = (price: string | null) => (price ? `${price} per company` : 'one flat price per company');
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const ARTICLE_META: Record<ArticlePath, ArticleMeta> = {
  '/compare/raremargin-vs-rillet': {
    title: 'RareMargin vs Rillet (2026): price, AI and close compared',
    description: () =>
      'RareMargin vs Rillet, from each company’s own website: published price, setup, AI approvals, integrations, SOC reports, SSO, and which team each one fits.',
    name: 'RareMargin vs Rillet',
    kind: 'compare',
    published: UPDATED,
    updated: UPDATED,
    mentions: [{ name: 'Rillet', url: 'https://www.rillet.com/' }],
  },
  '/compare/raremargin-vs-quickbooks-online': {
    title: 'RareMargin vs QuickBooks Online (2026) for SaaS finance',
    description: () =>
      'RareMargin vs QuickBooks Online for SaaS companies: list prices, user limits, ASC 606 revenue recognition, multiple entities, bank feeds, and when each one fits.',
    name: 'RareMargin vs QuickBooks Online',
    kind: 'compare',
    published: UPDATED,
    updated: UPDATED,
    mentions: [{ name: 'QuickBooks Online', url: 'https://quickbooks.intuit.com/online/' }],
  },
  '/compare/raremargin-vs-netsuite': {
    title: 'RareMargin vs NetSuite (2026): cost, setup and fit for SaaS',
    description: () =>
      'RareMargin vs NetSuite for SaaS finance teams: how each is priced and set up, ASC 606 revenue recognition, what each covers, and which company each one fits.',
    name: 'RareMargin vs NetSuite',
    kind: 'compare',
    published: UPDATED,
    updated: UPDATED,
    mentions: [{ name: 'NetSuite', url: 'https://www.netsuite.com/' }],
  },
  '/docs/asc-606-revenue-recognition': {
    title: 'ASC 606 revenue recognition in RareMargin, step by step',
    description: () =>
      'How RareMargin applies ASC 606 and IFRS 15: contracts, allocation by standalone selling price, recognition patterns, billing, deferred revenue, terminations.',
    name: 'ASC 606 revenue recognition',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
  },
  '/docs/how-continuous-close-works': {
    title: 'How continuous close works in RareMargin: posts and controls',
    description: () =>
      'Continuous close in RareMargin: what posts by itself, the 11 controls it checks, the close checklist, accrual suggestions, flux analysis and closed periods.',
    name: 'How continuous close works',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
  },
  '/docs/ai-journal-entries-and-approvals': {
    title: 'AI journal entries and approvals in RareMargin',
    description: () =>
      'How RareMargin’s AI assistant and MCP clients draft journal entries and accruals, why they can’t post them, and how approval rules and segregation of duties work.',
    name: 'AI journal entries and approvals',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
  },
  '/docs/api-mcp-server-and-webhooks': {
    title: 'RareMargin API, MCP server and webhooks: the reference',
    description: () =>
      'RareMargin’s REST API at /v1, MCP server at /mcp and signed webhooks: authentication, endpoints, idempotency, errors, the MCP tools, event types and retries.',
    name: 'API, MCP server and webhooks',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
  },
  '/docs/use-raremargin-in-claude-chatgpt-and-cursor': {
    title: 'RareMargin in Claude, ChatGPT, Cursor and Codex: MCP setup',
    description: () =>
      'Connect RareMargin’s MCP server to claude.ai, ChatGPT, Claude Code, Cursor or Codex by signing in or with an API key: setup, roles and safety.',
    name: 'Claude, ChatGPT and Cursor setup',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
    mentions: [
      { name: 'Claude', url: 'https://claude.ai/' },
      { name: 'ChatGPT', url: 'https://chatgpt.com/' },
      { name: 'Cursor', url: 'https://cursor.com/' },
      { name: 'Codex', url: 'https://developers.openai.com/codex' },
    ],
  },
  '/docs/set-up-raremargin-and-move-your-books': {
    title: 'Set up RareMargin and move your books in, step by step',
    description: () =>
      'How long RareMargin takes to set up, what sign-up asks for, moving your books from QuickBooks or spreadsheets, the bank CSV format, roles and subscribing.',
    name: 'Setting up RareMargin',
    kind: 'guide',
    published: UPDATED,
    updated: UPDATED,
  },
  '/about': {
    title: 'About RareMargin: who makes it and how it works',
    description: (f, price) =>
      `RareMargin is an AI-native general ledger for SaaS finance teams, ${price ? `at ${per(price)}` : 'with one plan'}. Who makes it, where it runs, how it keeps books trustworthy, and its limits.`,
    name: 'About',
    kind: 'about',
    published: UPDATED,
    updated: UPDATED,
  },
};

/** The 17 tools the assistant and MCP clients share (apps/server/src/ai/tools.ts). */
const TOOLS: [string, string][] = [
  ['get_company_overview', 'Cash, burn, runway, MRR, AR and AP, close status and what needs attention'],
  ['list_accounts', 'The chart of accounts'],
  ['get_financial_report', 'Any report, from the trial balance to SaaS metrics'],
  ['search_journal_entries', 'Find entries by memo, account, dates, status or source'],
  ['list_customers', 'Customers and what they owe'],
  ['list_open_invoices', 'Invoices not yet paid'],
  ['list_vendors', 'Vendors and what is owed to them'],
  ['list_open_bills', 'Bills not yet paid'],
  ['list_unmatched_bank_transactions', 'Unmatched bank lines, with ranked match suggestions and reasons'],
  ['run_controls', 'The 11 continuous controls and their evidence'],
  ['detect_anomalies', 'Duplicates, outliers and risky manual entries'],
  ['flux_analysis', 'Changes by account against the prior period, with what drove them'],
  ['close_status', 'The close checklist and what is left'],
  ['accrual_suggestions', 'Recurring vendors that haven’t billed this month'],
  ['verify_ledger', 'Checks the SHA-256 hash chain over posted entries'],
  ['propose_journal_entry', 'Drafts a journal entry for a person to approve'],
  ['propose_accrual', 'Drafts an accrual, reversing next month, for approval'],
];

const EVENTS = [
  'account.created, account.updated, subsidiary.created',
  'journal_entry.created, journal_entry.posted, journal_entry.rejected, journal_entry.reversed',
  'book_period.closed, book_period.reopened',
  'approval.requested, approval.approved, approval.rejected',
  'customer.created, customer.updated, product.created',
  'contract.created, contract.activated, contract.terminated, contract.completed',
  'invoice.created, invoice.issued, invoice.paid, invoice.voided, payment.created',
  'vendor.created, bill.created, bill.approved, bill.rejected, bill.paid, bill.voided',
  'bank_account.created, bank_transactions.imported, bank_transaction.matched, bank_transaction.unmatched',
];

const VERIFY = `import { createHmac, timingSafeEqual } from 'node:crypto';

// headers: the request's headers, lower-cased; body: the raw request body as a string.
function verify(secret, headers, body) {
  const id = headers['x-raremargin-id'];
  const ts = Number(headers['x-raremargin-timestamp']);
  if (!(Math.abs(Date.now() / 1000 - ts) <= 300)) return false;
  const expected = 'v1=' + createHmac('sha256', secret).update(\`\${id}.\${ts}.\${body}\`).digest('base64');
  const got = headers['x-raremargin-signature'] ?? '';
  return got.length === expected.length && timingSafeEqual(Buffer.from(got), Buffer.from(expected));
}`;

export const ARTICLES: Record<ArticlePath, (f: Facts, price: string | null) => Article> = {
  '/compare/raremargin-vs-rillet': (f, price) => ({
    eyebrow: 'Compare',
    h1: 'RareMargin vs Rillet',
    lead: `RareMargin and Rillet are both AI-native general ledgers built for continuous close. Rillet is sold through a demo, with a CPA-led implementation, native integrations and SOC 1 and SOC 2 Type II reports. RareMargin is self-serve at ${per(price)}, and its AI can only propose entries that a person approves.`,
    facts: {
      head: ['', 'RareMargin', 'Rillet'],
      rows: [
        ['Price', `${cap(per(price))}, published, every feature included`, 'Not published; the pricing page books a demo with sales'],
        ['How you start', `Sign up and use it the same day, with a ${f.trialDays}-day free trial and no card`, 'Book a demo with sales'],
        ['Implementation', 'Self-serve, from your own books or a sample company', '4–6 weeks, led by CPAs, according to Rillet'],
        ['AI', 'An assistant that answers questions and drafts entries; a person approves each draft before it posts', 'Aura AI, an assistant you can ask about the general ledger'],
        ['Integrations', 'REST API, MCP server and signed webhooks; bank lines by CSV or the API', 'Native integrations, including Salesforce, Stripe, HubSpot, Ramp, Brex, BILL, Rippling and Plaid'],
        ['Security reports', 'None yet', 'SOC 1 and SOC 2 Type II'],
        ['Sign-in', 'Email and password, with optional two-factor sign-in through an authenticator app', 'SSO through Google, Microsoft Entra or Rippling'],
        ['Customers', 'New: launched in 2026', '800+ customers in 70+ countries, according to Rillet'],
      ],
    },
    sections: [
      {
        id: 'difference',
        q: 'What is the difference between RareMargin and Rillet?',
        a: 'Rillet is a sales-led ERP with a CPA-led implementation and audited security reports; RareMargin is a self-serve general ledger with a published price.',
        more: [
          'Both keep the books closed through the month instead of catching up at month-end, both are built around SaaS revenue, and both have an AI assistant. They differ in how you buy them, how much is done for you, and how far the AI may go.',
          'Rillet’s team implements the system and takes ownership of the data migration. Its website quotes 4–6 weeks led by CPAs, and support from a customer success manager and CPA team in a dedicated Slack channel. RareMargin is set up by your own team: sign up, start from your books or from a sample SaaS company with 14 months of history, and subscribe when the books are in.',
        ],
      },
      {
        id: 'alternative',
        q: 'Is RareMargin a Rillet alternative?',
        a: `Yes, for SaaS finance teams that want to start without a sales process: RareMargin is the self-serve alternative to Rillet, at ${per(price)} with a ${f.trialDays}-day trial.`,
        more: [
          'RareMargin covers the same core: a double-entry general ledger across entities and currencies, ASC 606 revenue schedules from contracts, bills and accruals, bank matching, a close checklist with continuous controls, and an AI assistant. It does not yet match Rillet’s integrations, security reports or single sign-on, covered below.',
        ],
      },
      {
        id: 'price',
        q: 'How much do RareMargin and Rillet cost?',
        a: `RareMargin costs ${per(price)}, with every feature included. Rillet doesn’t publish its prices; its pricing page asks you to book a demo with sales.`,
        more: [
          `RareMargin’s plan includes the ledger, revenue and AR, spend and AP, cash, the close, 13 reports, the AI assistant (${f.aiMonthlyMessages.toLocaleString('en-US')} messages a month) and the API, with no per-user fee. Dodo Payments, the merchant of record, takes the payment, and a subscription can be cancelled at any time.`,
        ],
      },
      {
        id: 'security',
        q: 'Which is better if you need SOC 2 reports or SSO?',
        a: 'Rillet. It shows SOC 1 and SOC 2 Type II reports and offers SSO through Google, Microsoft Entra and Rippling; RareMargin has neither yet.',
        more: [
          'Rillet’s security page also lists AES-256 encryption at rest, TLS 1.2 or later in transit, hosting on AWS, automated monitoring and penetration tests.',
          'RareMargin has optional two-factor sign-in, four roles (owner, admin, accountant and viewer), approval rules with segregation of duties, and a SHA-256 hash chain over every posted entry, so a change to any past entry shows. If your auditors or customers ask for a SOC 2 report today, choose Rillet.',
        ],
      },
      {
        id: 'integrations',
        q: 'Which has more integrations?',
        a: 'Rillet. Its integrations page lists native connections to tools such as Salesforce, Stripe, HubSpot, Ramp, Brex, BILL and Plaid; RareMargin connects through its API instead.',
        more: [
          'RareMargin has a REST API at /v1, webhooks for 34 event types and an MCP server for AI agents. Bank transactions come in by CSV upload or through the API, because RareMargin has no direct bank connections yet. A team that wants its billing, CRM and spend tools connected without writing code is better served by Rillet today.',
        ],
      },
      {
        id: 'ai',
        q: 'How do the AI assistants differ?',
        a: 'RareMargin’s assistant can read the books and draft entries, but it can’t post them: each draft waits in the approval queue for a person with the right role.',
        more: [
          'The same rule covers any AI agent connected through RareMargin’s MCP server. Of the 17 tools the assistant and MCP clients share, 15 only read and 2 propose a journal entry or an accrual. Agents and API keys can’t approve anything, and nobody can approve an entry they submitted.',
          'Rillet’s assistant is Aura AI, which answers questions about the general ledger. The Rillet pages we read don’t say what Aura may change in the books, so ask in the demo.',
        ],
      },
      {
        id: 'choose',
        q: 'Should I choose RareMargin or Rillet?',
        a: 'Choose Rillet for a managed implementation, native integrations and SOC 2 reports now; choose RareMargin to start today, on a published price, with AI that only proposes.',
        more: [
          {
            list: [
              'Rillet fits a company that wants its ERP implemented and its data migrated for it, needs SOC reports or single sign-on, or relies on integrations with tools such as Salesforce and Stripe.',
              `RareMargin fits a SaaS finance team that wants to try the product on its own data today, pay ${per(price)}, keep AI agents behind approvals, and connect its own tools through an API, webhooks and MCP.`,
            ],
          },
        ],
      },
    ],
    sources: [
      { label: 'Rillet: home page', url: 'https://www.rillet.com/' },
      { label: 'Rillet: pricing, with its comparison to NetSuite', url: 'https://www.rillet.com/pricing' },
      { label: 'Rillet: security', url: 'https://www.rillet.com/security' },
      { label: 'Rillet: integrations', url: 'https://www.rillet.com/integrations' },
    ],
    related: ['/compare/raremargin-vs-netsuite', '/compare/raremargin-vs-quickbooks-online', '/docs/ai-journal-entries-and-approvals', '/pricing'],
  }),

  '/compare/raremargin-vs-quickbooks-online': (f, price) => ({
    eyebrow: 'Compare',
    h1: 'RareMargin vs QuickBooks Online',
    lead: `QuickBooks Online is small-business accounting, with low entry prices, direct bank connections and decades of Intuit behind it. RareMargin is a general ledger for SaaS finance teams, with ASC 606 revenue schedules, multi-entity consolidation and a continuous close in one plan at ${per(price)}.`,
    facts: {
      head: ['', 'RareMargin', 'QuickBooks Online'],
      rows: [
        ['List price a month', cap(per(price)), 'Simple Start $38, Essentials $85, Plus $140, Advanced $340, often discounted for the first months'],
        ['Users', 'No per-user fee', '1 on Simple Start, 3 on Essentials, 5 on Plus, 25 on Advanced'],
        ['ASC 606 revenue recognition', 'In the plan: schedules from contracts, allocated by standalone selling price', 'Listed among the Advanced plan’s features'],
        ['More than one entity', 'Subsidiaries in one company, consolidated, with intercompany balances eliminated', 'Intuit points multi-entity organizations to Intuit Enterprise Suite'],
        ['Bank transactions', 'CSV upload or the API; no direct bank connections yet', 'Direct bank connections'],
        ['AI', 'Drafts entries and accruals; a person approves each before it posts', 'Categorizes expenses and answers questions in chat, some of it in beta'],
        ['Track record', 'New: launched in 2026', '40+ years and millions of customers, according to Intuit'],
      ],
    },
    sections: [
      {
        id: 'alternative',
        q: 'Is RareMargin a QuickBooks Online alternative?',
        a: 'Yes, for SaaS companies that have outgrown it: RareMargin is the QuickBooks Online alternative that adds ASC 606 revenue schedules, multi-entity consolidation and continuous controls.',
        more: [
          'QuickBooks Online is built for small businesses in general: invoices and estimates, expenses, sales tax and bank connections. SaaS companies tend to outgrow it when deferred revenue, several entities or currencies, and month-end reconciliations start living in spreadsheets beside it.',
          'RareMargin keeps those inside the ledger. Each customer contract becomes a revenue schedule, subsidiaries consolidate into a reporting currency with intercompany balances eliminated, and 11 controls check the books continuously.',
        ],
      },
      {
        id: 'price',
        q: 'How much do RareMargin and QuickBooks Online cost?',
        a: `RareMargin costs ${per(price)}, with every feature and no per-user fee. QuickBooks Online lists $38 to $340 a month, depending on the plan and how many users you need.`,
        more: [
          {
            table: {
              head: ['QuickBooks Online plan', 'List price a month', 'Users'],
              rows: [
                ['Simple Start', '$38', '1'],
                ['Essentials', '$85', '3'],
                ['Plus', '$140', '5'],
                ['Advanced', '$340', '25'],
              ],
            },
          },
          'These are Intuit’s US list prices before its introductory discounts, as its website showed them on the date below.',
        ],
      },
      {
        id: 'quickbooks-fit',
        q: 'When is QuickBooks Online the better choice?',
        a: 'When you run a small business that needs simple bookkeeping, direct bank connections, sales tax and invoicing, for less than RareMargin costs.',
        more: [
          'QuickBooks Online also comes with Intuit’s 40-plus years in accounting software, millions of customers and app integrations. If you have one entity and no revenue to defer, it is likely the simpler choice.',
        ],
      },
      {
        id: 'raremargin-fit',
        q: 'When is RareMargin the better choice?',
        a: 'When you recognize subscription revenue under ASC 606, run more than one entity or currency, and want month-end to be a review rather than a rebuild.',
        more: [
          {
            list: [
              'Revenue: contracts become schedules that post recognized revenue as each service period arrives, and deferred revenue is checked against billings less recognition.',
              'Entities and currencies: each subsidiary keeps its own functional currency and consolidates into the reporting currency, with the translation difference in CTA.',
              'Close: a checklist of 11 tasks, 8 of which check themselves, and 11 controls that run continuously.',
              'AI with approvals: the assistant drafts entries, and a person approves each one before it posts.',
              'Developers: a REST API, signed webhooks and an MCP server, in the one plan.',
            ],
          },
        ],
      },
      {
        id: 'switch',
        q: 'How do I move from QuickBooks Online to RareMargin?',
        a: 'Set up your accounts, post opening balances as a journal entry, add customers, contracts and vendors, and import bank lines from CSV; RareMargin has no one-click import yet.',
        more: [
          'Most of this can be scripted against RareMargin’s REST API, which creates accounts, journal entries, customers, contracts, vendors and bills. Run both systems side by side for a month before you switch, and compare the trial balances. The guide to setting up RareMargin covers each step and the bank CSV format.',
        ],
      },
    ],
    sources: [
      { label: 'QuickBooks Online: plans and pricing', url: 'https://quickbooks.intuit.com/pricing/' },
      { label: 'QuickBooks Online Plus', url: 'https://quickbooks.intuit.com/online/plus/' },
      { label: 'QuickBooks Online Advanced', url: 'https://quickbooks.intuit.com/online/advanced/' },
    ],
    related: ['/docs/set-up-raremargin-and-move-your-books', '/compare/raremargin-vs-netsuite', '/compare/raremargin-vs-rillet', '/pricing'],
  }),

  '/compare/raremargin-vs-netsuite': (f, price) => ({
    eyebrow: 'Compare',
    h1: 'RareMargin vs NetSuite',
    lead: `NetSuite is a full cloud ERP, sold on an annual license with a one-time implementation fee. RareMargin is a general ledger and continuous close for SaaS finance teams, self-serve at ${per(price)}, published.`,
    facts: {
      head: ['', 'RareMargin', 'NetSuite'],
      rows: [
        ['Pricing', `${cap(per(price))}, published, every feature included`, 'An annual license made of the core platform, optional modules and the number of users, plus a one-time implementation fee; no public price'],
        ['How you start', `Self-serve sign-up and a ${f.trialDays}-day free trial, no card`, 'Through sales, with a guided test drive'],
        ['Scope', 'General ledger, revenue and AR, AP, cash, close, controls and reports', 'A full ERP suite, including order management and fulfillment'],
        [
          'Revenue recognition',
          'ASC 606 schedules from contracts, allocated by standalone selling price, recognized daily, evenly by period or at a point in time',
          'Rules per product or service, allocation by standalone selling price, and subscription, fulfillment, milestone, percentage-of-completion and time-and-materials models',
        ],
        ['Customers', 'New: launched in 2026', '44,000+ businesses, according to NetSuite'],
      ],
    },
    sections: [
      {
        id: 'difference',
        q: 'What is the difference between RareMargin and NetSuite?',
        a: 'NetSuite is an ERP that runs a whole business, from orders and fulfillment to the financials; RareMargin is only the finance part: the ledger, revenue, AP, cash and the close.',
        more: [
          'That difference decides most of the rest. NetSuite is fitted to each company through modules and an implementation project. RareMargin has one plan, the same for every company, which a finance team sets up itself.',
        ],
      },
      {
        id: 'alternative',
        q: 'Is RareMargin a NetSuite alternative for SaaS companies?',
        a: 'Yes, for SaaS companies that need the finance side of an ERP without an ERP project: RareMargin covers the ledger, ASC 606 revenue, multiple entities and the close.',
        more: [
          'It is not a replacement where NetSuite runs operations too. If you need order management, fulfillment or other modules beyond finance, RareMargin doesn’t have them.',
        ],
      },
      {
        id: 'cost',
        q: 'How much does NetSuite cost compared with RareMargin?',
        a: `NetSuite doesn’t publish prices: you pay an annual license plus a one-time implementation fee. RareMargin costs ${per(price)}, published on its pricing page.`,
        more: [
          'According to NetSuite, the license has three parts: the core platform, optional modules and the number of users. Because those are quoted for each company, the only way to compare is to get a quote. RareMargin’s price is the same for every company and includes every feature, the AI assistant and the API.',
        ],
      },
      {
        id: 'revenue',
        q: 'Which handles ASC 606 revenue recognition better?',
        a: 'NetSuite covers more revenue models; RareMargin covers subscription and service contracts, with the schedules and deferred revenue built into the ledger.',
        more: [
          'NetSuite lists recognition for subscriptions, fulfillment by quantity or date, project milestones or percentage of completion, and time and materials. RareMargin allocates each contract by relative standalone selling price and recognizes each line daily, evenly by period or at a point in time. If you sell hardware, projects or usage-based services alongside software, NetSuite is the safer fit.',
        ],
      },
      {
        id: 'netsuite-fit',
        q: 'When should a company choose NetSuite?',
        a: 'When it needs one system for operations as well as finance, such as order management and fulfillment, or expects to customize the system heavily.',
        more: ['NetSuite also has the longer track record: it reports more than 44,000 customers.'],
      },
      {
        id: 'raremargin-fit',
        q: 'When should a company choose RareMargin?',
        a: 'When it is a SaaS company whose finance team wants ASC 606 revenue, multi-entity books and a continuous close without an ERP project, at a published price.',
        more: [`You can start the same day, on your own books or on a sample SaaS company with 14 months of history, and use everything free for ${f.trialDays} days.`],
      },
    ],
    sources: [
      { label: 'NetSuite: home page', url: 'https://www.netsuite.com/portal/home.shtml' },
      { label: 'NetSuite: revenue recognition, with how licenses are priced', url: 'https://www.netsuite.com/portal/products/erp/financial-management/revenue-management.shtml' },
    ],
    related: ['/compare/raremargin-vs-rillet', '/compare/raremargin-vs-quickbooks-online', '/docs/asc-606-revenue-recognition', '/pricing'],
  }),

  '/docs/asc-606-revenue-recognition': () => ({
    eyebrow: 'Guide',
    h1: 'ASC 606 revenue recognition in RareMargin',
    lead: 'RareMargin turns each customer contract into a revenue schedule. It allocates the contract price across the contract’s lines by relative standalone selling price, then posts recognized revenue to the ledger as each service period arrives.',
    facts: {
      head: ['ASC 606 step', 'What RareMargin does'],
      rows: [
        ['1. Identify the contract', 'You enter the customer contract: the customer, its term and how often it is billed'],
        ['2. Identify the performance obligations', 'Each contract line is a product, such as a subscription or an implementation'],
        ['3. Determine the transaction price', 'The sum of the contract lines’ amounts'],
        ['4. Allocate the price', 'Automatic, by relative standalone selling price (SSP)'],
        ['5. Recognize revenue', 'Automatic, from each product’s pattern: daily, evenly by period, or at a point in time'],
      ],
    },
    sections: [
      {
        id: 'five-steps',
        q: 'What are the five steps of ASC 606?',
        a: 'Identify the contract, identify its performance obligations, determine the transaction price, allocate that price to the obligations, and recognize revenue as each is satisfied.',
        more: ['IFRS 15, the international standard, uses the same five steps. In RareMargin, steps 1 to 3 are the contract you enter, and steps 4 and 5 happen automatically, as the table above shows.'],
      },
      {
        id: 'allocation',
        q: 'How does RareMargin allocate the transaction price?',
        a: 'By relative standalone selling price: each line gets the share of the contract price that its SSP is of the total SSP, and a line with no SSP uses its own contract amount.',
        more: [
          'For example, a one-year contract for $30,000 has two lines: the platform subscription, with an SSP of $27,000, and implementation, with an SSP of $6,000.',
          {
            table: {
              head: ['Line', 'SSP', 'Share', 'Allocated'],
              rows: [
                ['Platform subscription', '$27,000', '81.8%', '$24,545.45'],
                ['Implementation', '$6,000', '18.2%', '$5,454.55'],
                ['Total', '$33,000', '100%', '$30,000.00'],
              ],
            },
          },
          'Amounts are kept in cents, and the allocated amounts always add up to the contract price exactly. A recurring product’s SSP is a monthly price, scaled to the line’s quantity and term; a one-off product’s SSP is per unit.',
        ],
      },
      {
        id: 'patterns',
        q: 'How does RareMargin recognize revenue over time or at a point in time?',
        a: 'Each product has a revenue pattern, daily, even by period or point in time, and RareMargin builds every contract line’s schedule from it.',
        more: [
          {
            list: [
              'Daily: revenue follows the number of service days in each month, so a 31-day month earns more than a 28-day one.',
              'Even by period: each full month earns the same amount, and a partial first or last month earns its share of the days.',
              'Point in time: the whole amount is recognized on the line’s start date, for example a one-off setup fee.',
            ],
          },
          'Each schedule line is dated the last day of its month, or the service end date when that comes first. The lines always add up to the allocated amount, to the cent.',
        ],
      },
      {
        id: 'billing',
        q: 'How do invoices and deferred revenue work in RareMargin?',
        a: 'Invoices follow their own billing schedule, monthly, quarterly, annual or upfront, so billing never has to match recognition; the difference sits in deferred revenue.',
        more: [
          'Contract invoices are issued in advance at the start of each billing period. Issuing one debits accounts receivable and credits deferred revenue, and each schedule line moves its amount from deferred revenue into revenue when it posts.',
          'A control checks continuously that the deferred revenue balance in the ledger equals billings less recognized revenue, so a stray manual entry to the account shows up straight away.',
        ],
      },
      {
        id: 'posting',
        q: 'When does recognized revenue post to the ledger?',
        a: 'As soon as its date arrives: a background job posts every due schedule line, so revenue is in the books before month-end instead of being booked at the close.',
        more: [
          'A line dated in a period that is already closed posts in the current period instead, so closed books never reopen. The close checklist’s “Revenue recognition posted” task ticks itself once every line dated in the period has posted.',
        ],
      },
      {
        id: 'termination',
        q: 'What happens when a contract ends early?',
        a: 'Terminating a contract in RareMargin keeps the revenue earned through the termination date, cancels the rest of the schedule and voids invoices scheduled after it.',
        more: ['Anything already billed but not yet earned stays in deferred revenue until you refund it or credit it to the customer.'],
      },
      {
        id: 'reports',
        q: 'Which reports come from the revenue schedules?',
        a: 'The deferred revenue report, revenue by customer, and the SaaS metrics report with MRR, ARR, NRR and GRR all read the same contracts and posted entries.',
      },
    ],
    sources: [{ label: 'IFRS Foundation: IFRS 15 Revenue from Contracts with Customers', url: 'https://www.ifrs.org/issued-standards/list-of-standards/ifrs-15-revenue-from-contracts-with-customers/' }],
    related: ['/docs/how-continuous-close-works', '/compare/raremargin-vs-quickbooks-online', '/compare/raremargin-vs-netsuite', '/pricing'],
  }),

  '/docs/how-continuous-close-works': () => ({
    eyebrow: 'Guide',
    h1: 'How continuous close works',
    lead: 'Continuous close means month-end work happens as the month goes. RareMargin posts revenue, amortization, accrual reversals and scheduled invoices the day they fall due, checks 11 controls continuously, and leaves a review for month-end.',
    facts: {
      head: ['', 'Month-end close', 'Continuous close in RareMargin'],
      rows: [
        ['Revenue and amortization', 'Booked from spreadsheets after the month ends', 'Posted from schedules the day each line falls due'],
        ['Accruals', 'Found by reading through last month’s bills', 'Suggested when a regular vendor hasn’t billed, and reversed the next month'],
        ['Bank matching', 'Done in a batch at the end', 'Suggested for each bank line, with a score and the reasons for it'],
        ['Controls', 'Checked once, during the close', 'Eleven checks that run continuously'],
        ['Month-end', 'Catching up', 'Review and sign-off'],
      ],
    },
    sections: [
      {
        id: 'what',
        q: 'What is continuous close?',
        a: 'Continuous close keeps the books closed through the month: entries post as their dates arrive and controls run all the time, so month-end is a review, not a catch-up.',
        more: ['The smaller the gap between when something happens and when it reaches the ledger, the less is left to do when the month ends, and the sooner the numbers can be trusted.'],
      },
      {
        id: 'automatic',
        q: 'What does RareMargin post automatically?',
        a: 'Scheduled contract invoices, recognized revenue, prepaid amortization and the reversals of last month’s accruals, each as soon as its date arrives.',
        more: ['A background job finds each company’s due lines and posts them, claiming each line in the same transaction that posts it, so running the job twice never posts anything twice. A line dated in a closed period posts in the current period instead of reopening the books.'],
      },
      {
        id: 'controls',
        q: 'Which controls does RareMargin check?',
        a: 'Eleven, from the trial balance and subledger tie-outs to the hash chain and stale approvals; each reports pass, warn or fail with the evidence behind it.',
        more: [
          {
            table: {
              head: ['Control', 'What it checks'],
              rows: [
                ['Trial balance balances', 'Debits equal credits across the ledger'],
                ['Report balances match posted lines', 'The balances reports read from equal the sum of the posted lines'],
                ['Ledger hash chain intact', 'No posted entry was changed after it posted'],
                ['AR subledger ties to the general ledger', 'Open invoices less unapplied cash equal the receivables balance'],
                ['AP subledger ties to the general ledger', 'Open bills equal the payables balance'],
                ['Deferred revenue ties to billings less recognition', 'The deferred revenue balance equals billed less recognized revenue'],
                ['Bank accounts reconciled', 'Each bank account’s imported balance matches the ledger, with unmatched lines listed'],
                ['No postings into closed periods', 'Nothing has posted into a period after it closed'],
                ['Suspense account is clear', 'Nothing is left parked in suspense'],
                ['Intercompany balances net to zero', 'Balances between subsidiaries cancel out'],
                ['Approvals decided within 3 days', 'No approval request has waited longer than 3 days'],
              ],
            },
          },
        ],
      },
      {
        id: 'checklist',
        q: 'What is on RareMargin’s close checklist?',
        a: 'Eleven tasks, from matching bank lines to the management reporting package; eight of them tick themselves when the books show they are done.',
        more: [
          {
            table: {
              head: ['Task', 'Ticks itself'],
              rows: [
                ['Match all bank transactions', 'Yes'],
                ['Record customer payments and review AR aging', 'No'],
                ['Issue all invoices for the period', 'Yes'],
                ['Revenue recognition posted', 'Yes'],
                ['Enter and approve all vendor bills', 'Yes'],
                ['Prepaid amortization posted', 'Yes'],
                ['Book accruals for unbilled spend', 'Yes'],
                ['Approve pending journal entries', 'Yes'],
                ['Review flux analysis', 'No'],
                ['All controls passing', 'Yes'],
                ['Management reporting package', 'No'],
              ],
            },
          },
        ],
      },
      {
        id: 'accruals',
        q: 'How does RareMargin suggest accruals?',
        a: 'When a vendor that billed in at least 2 of the last 3 months has no bill dated this month and no accrual booked, RareMargin suggests accruing its average monthly spend.',
        more: ['Each suggestion lists the bills it is based on. You or the assistant can turn it into an accrual entry, which goes through approval like any other entry and reverses itself on the first day of the next month.'],
      },
      {
        id: 'flux',
        q: 'What does RareMargin’s flux analysis flag?',
        a: 'Accounts that moved against the prior period or prior year by at least a quarter of your materiality, or by 20% and at least 5% of materiality.',
        more: ['Materiality is a company setting. Each flagged account names up to three vendors, customers or entries that drove the change, in a sentence that explains it.'],
      },
      {
        id: 'locks',
        q: 'Can a closed period change?',
        a: 'Nothing can post into a closed period: RareMargin posts late items in the current period instead, and a control checks that nothing slipped through.',
        more: ['A period can be reopened, and reopening is recorded as an event, so it stays visible to reviewers and to webhook subscribers.'],
      },
    ],
    related: ['/docs/asc-606-revenue-recognition', '/docs/ai-journal-entries-and-approvals', '/compare/raremargin-vs-rillet', '/pricing'],
  }),

  '/docs/ai-journal-entries-and-approvals': (f) => ({
    eyebrow: 'Guide',
    h1: 'AI journal entries and approvals in RareMargin',
    lead: 'RareMargin’s AI assistant, and any AI agent connected through its MCP server, can read the books and propose journal entries and accruals, but can’t post them. Every proposal waits in the approval queue until a person with the right role approves it.',
    facts: {
      head: ['', 'People', 'API keys', 'AI assistant and MCP clients'],
      rows: [
        ['Read the books', 'By role', 'By the key’s role', 'Yes'],
        ['Propose entries', 'Yes', 'Yes', 'Yes'],
        ['Post without approval', 'When no approval rule applies', 'When no approval rule applies', 'Never'],
        ['Approve or reject', 'With the required role, and not their own by default', 'Never', 'Never'],
      ],
    },
    sections: [
      {
        id: 'can-ai-post',
        q: 'Can RareMargin’s AI post entries to the ledger?',
        a: 'No. The assistant and MCP clients can only propose; their entries always wait for a person, whatever approval rules the company has set.',
        more: [
          'This is enforced where entries are created, not in the chat window: an entry from the assistant or an MCP client is held for approval even where no rule would hold a person’s entry. Approving is refused to agents and API keys outright, with the message “Only a person can approve or reject.”',
        ],
      },
      {
        id: 'tools',
        q: 'What can RareMargin’s assistant do?',
        a: 'It has 17 tools: 15 read the books, from statements to anomaly checks, and 2 write, proposing a journal entry or an accrual for approval.',
        more: [{ table: { head: ['Tool', 'What it does'], rows: TOOLS } }],
      },
      {
        id: 'rules',
        q: 'How do approval rules work?',
        a: 'An admin sets rules for journal entries and bills by minimum amount, accounts, vendors and source; when several match, the one needing the most senior role applies.',
        more: [
          'The roles, from most to least senior, are owner, admin, accountant and viewer. A rule can, for example, send every entry over $10,000 to an admin, or every bill from one vendor to the owner. The source is where an entry came from: the app, the API, the assistant, an MCP client or an import.',
        ],
      },
      {
        id: 'segregation',
        q: 'How does RareMargin enforce segregation of duties?',
        a: 'Nobody can approve a request they submitted. A company can allow self-approval in its settings, for a team of one, but it is off by default.',
      },
      {
        id: 'record',
        q: 'What is recorded when an entry is approved?',
        a: 'Who submitted it and from where, who approved or rejected it and when, and any comment; approving posts the entry in the same transaction.',
        more: ['Each step is also an event (approval.requested, approval.approved, approval.rejected) that webhooks can deliver to your own systems. Once posted, the entry joins the SHA-256 hash chain, so any later change to it can be detected.'],
      },
      {
        id: 'model',
        q: 'Which AI model does RareMargin use, and what does it see?',
        a: 'An OpenAI model, through OpenAI’s API. It receives your question and the parts of the books it reads to answer; OpenAI doesn’t train on it and keeps it up to 30 days.',
        more: ['OpenAI is listed as a subprocessor in RareMargin’s privacy policy, with what it receives.'],
      },
      {
        id: 'allowance',
        q: 'How many assistant messages are included?',
        a: `${f.aiMonthlyMessages.toLocaleString('en-US')} a month on a subscription, and ${f.aiTrialMessages} during the free trial. Each question is one message, however many tools the assistant uses to answer it.`,
      },
    ],
    related: ['/docs/api-mcp-server-and-webhooks', '/docs/how-continuous-close-works', '/compare/raremargin-vs-rillet', '/pricing'],
  }),

  '/docs/api-mcp-server-and-webhooks': (f) => ({
    eyebrow: 'Guide',
    h1: 'The RareMargin API, MCP server and webhooks',
    lead: 'RareMargin has a versioned REST API at /v1, an MCP server at /mcp for AI agents, and webhooks signed with HMAC-SHA256. All three run on the same services and approval rules as the app, so a script gets exactly the rules a person does.',
    facts: {
      head: ['', 'Address', 'For'],
      rows: [
        ['REST API', `${f.origin}/v1`, 'Scripts, integrations and data exports'],
        ['MCP server', `${f.origin}/mcp`, 'Claude and other AI agents'],
        ['Webhooks', 'Your HTTPS endpoint', 'Reacting to changes in the books'],
      ],
    },
    sections: [
      {
        id: 'auth',
        q: 'How do I authenticate with the RareMargin API?',
        a: 'Create an API key in Settings and send it as a Bearer token. Each key carries a role, and its writes go through the same approval rules as a person’s.',
        more: [{ code: `curl ${f.origin}/v1/accounts \\\n  -H "Authorization: Bearer $RAREMARGIN_API_KEY"` }],
      },
      {
        id: 'endpoints',
        q: 'What can the RareMargin REST API do?',
        a: 'It reads and writes the ledger and every subledger: accounts, journal entries, periods, customers, products, contracts, invoices, payments, vendors, bills and bank lines.',
        more: [
          {
            table: {
              head: ['Area', 'Endpoints under /v1'],
              rows: [
                ['Ledger', 'GET /subsidiaries; GET, POST /accounts; GET, POST /journal-entries; GET /journal-entries/:id; POST /journal-entries/:id/reverse'],
                ['Periods and controls', 'GET /book-periods; POST /book-periods/:period/close; GET /ledger/verify; GET /controls'],
                ['Revenue', 'GET, POST /customers; GET /customers/:id; GET, POST /products; GET, POST /contracts; GET /contracts/:id; POST /contracts/:id/activate'],
                ['Billing', 'GET, POST /invoices; GET /invoices/:id; POST /invoices/:id/issue; POST /invoices/:id/void; GET, POST /payments'],
                ['Spend', 'GET, POST /vendors; GET /vendors/:id; GET, POST /bills; GET /bills/:id; POST /bills/:id/payments'],
                ['Cash', 'GET /bank-accounts; GET, POST /bank-accounts/:id/transactions'],
                ['Reports and events', 'GET /reports/:name; GET /events; GET, POST /webhooks; DELETE /webhooks/:id'],
              ],
            },
          },
          'Reports include the trial balance, income statement, balance sheet, cash flow, general ledger, AR and AP aging, SaaS metrics, deferred revenue, revenue by customer, spend, cash position, burn and runway, and the cash forecast.',
        ],
      },
      {
        id: 'conventions',
        q: 'How does the API handle amounts, retries and errors?',
        a: 'Amounts are decimal strings, writes accept an Idempotency-Key, lists page with cursors, errors follow RFC 9457, and a write held for approval returns 202 Accepted.',
        more: ['JSON keys are snake_case. Each API key has its own rate limit, reported in X-RateLimit headers.'],
      },
      {
        id: 'mcp',
        q: 'How do I connect Claude or another AI agent to RareMargin?',
        a: `Add ${f.origin}/mcp to the client as a Streamable HTTP MCP server. A client that supports MCP sign-in opens RareMargin’s sign-in page; any other sends an API key as a Bearer token. The agent gets the assistant’s 17 tools.`,
        more: [
          'Sign-in is OAuth 2.1 with PKCE (S256). The server answers 401 with a WWW-Authenticate header pointing to /.well-known/oauth-protected-resource/mcp, publishes its authorization server metadata at /.well-known/oauth-authorization-server, and accepts dynamic client registration (RFC 7591) and client ID metadata documents. The scopes are books:read and books:write.',
          'With an API key, the settings file differs between clients; for most it looks like this:',
          { code: JSON.stringify({ mcpServers: { raremargin: { type: 'http', url: `${f.origin}/mcp`, headers: { Authorization: 'Bearer YOUR_API_KEY' } } } }, null, 2) },
          'Its two write tools, propose_journal_entry and propose_accrual, create approval requests, never postings, so an agent can prepare the work and a person decides what reaches the books.',
          'Exact setup for claude.ai, ChatGPT, Claude Code, Claude Desktop, Cursor and Codex is in the guide to using RareMargin in Claude, ChatGPT and Cursor.',
        ],
      },
      {
        id: 'events',
        q: 'Which webhook events does RareMargin send?',
        a: '34 event types, covering accounts, journal entries, periods, approvals, customers, contracts, invoices, payments, vendors, bills and bank lines.',
        more: [{ list: EVENTS }],
      },
      {
        id: 'signing',
        q: 'How are RareMargin webhooks signed and retried?',
        a: 'Each delivery carries X-RareMargin-Signature: v1= and a base64 HMAC-SHA256 of the event id, timestamp and body, keyed with your endpoint’s secret.',
        more: [
          'Deliveries also carry X-RareMargin-Id, X-RareMargin-Event and X-RareMargin-Timestamp (Unix seconds). Reject a delivery whose timestamp is more than 5 minutes off, and compare signatures in constant time:',
          { code: VERIFY },
          'A failed delivery is retried up to 8 times, 30 seconds after the first attempt and then twice as long each time, up to 6 hours. Events go out from a transactional outbox, and every delivery is logged in Settings.',
        ],
      },
    ],
    sources: [
      { label: 'Model Context Protocol', url: 'https://modelcontextprotocol.io/' },
      { label: 'RFC 9457: Problem Details for HTTP APIs', url: 'https://www.rfc-editor.org/rfc/rfc9457' },
    ],
    related: ['/docs/use-raremargin-in-claude-chatgpt-and-cursor', '/docs/ai-journal-entries-and-approvals', '/docs/asc-606-revenue-recognition', '/pricing'],
  }),

  '/docs/use-raremargin-in-claude-chatgpt-and-cursor': (f) => ({
    eyebrow: 'Guide',
    h1: 'Use RareMargin in Claude, ChatGPT and Cursor',
    lead: `RareMargin runs an MCP server at ${f.origin}/mcp, so an AI assistant can read your books and draft entries for a person to approve. claude.ai, Claude Desktop, Claude Code and ChatGPT connect by signing in to RareMargin, where you choose the company and what the assistant may do. Cursor and OpenAI’s Codex connect with a RareMargin API key.`,
    facts: {
      head: ['Assistant', 'Works today', 'How it connects'],
      rows: [
        ['claude.ai, web and mobile', 'Yes', 'A custom connector with the URL, then sign in to RareMargin'],
        ['Claude Desktop', 'Yes', 'The same custom connector, under Settings, Connectors'],
        ['Claude Code', 'Yes', 'One claude mcp add command, then sign in from /mcp; or an API key in a header'],
        ['ChatGPT', 'Yes', 'An app added in developer mode, then sign in to RareMargin'],
        ['Cursor', 'Yes', 'mcp.json, with an API key in a header'],
        ['OpenAI Codex', 'Yes', 'config.toml, with an API key in an environment variable'],
      ],
    },
    sections: [
      {
        id: 'claude',
        q: 'Can I use RareMargin from Claude?',
        a: `Yes. On claude.ai and in Claude Desktop, add a custom connector under Settings, Connectors with the URL ${f.origin}/mcp, then sign in to RareMargin when Claude asks. The connector then works in the Claude mobile apps too.`,
        more: ['Leave the OAuth client ID and secret empty: Claude registers itself with RareMargin. Claude Code connects with one command (below).'],
      },
      {
        id: 'chatgpt',
        q: 'Can I use RareMargin from ChatGPT?',
        a: `Yes. Turn on developer mode in ChatGPT’s settings, add an app with the URL ${f.origin}/mcp and OAuth sign-in, then sign in to RareMargin. OpenAI’s Codex connects with an API key read from an environment variable.`,
      },
      {
        id: 'sign-in',
        q: 'What happens when an assistant signs in to RareMargin?',
        a: 'RareMargin opens its own sign-in page, then asks which company the assistant may use and its role: Viewer only reads, and Accountant can also draft entries and accruals for approval. The assistant never sees your password.',
        more: [
          'It never gets more than your own role, and if your role drops or you leave the company, its access follows. It renews its access every hour while you use it. Every app you connect is listed in Settings, API & MCP, under Connected apps, where you or an admin can disconnect it at once.',
        ],
      },
      {
        id: 'tools',
        q: 'What can an AI assistant do in RareMargin?',
        a: 'It gets the same 17 tools as RareMargin’s built-in assistant: 15 read the books, and 2 draft a journal entry or an accrual that waits for a person to approve it.',
        more: [
          'So it can answer questions like these from your own books:',
          {
            list: [
              'What is our runway, and what changed in burn since last month?',
              'Which customers owe us money, and which invoices are overdue?',
              'Which bank lines are still unmatched, and what do they probably match?',
              'Did any control fail, and what is the evidence?',
              'Which recurring vendors haven’t billed this month? Draft accruals for them.',
            ],
          },
          { table: { head: ['Tool', 'What it does'], rows: TOOLS } },
        ],
      },
      {
        id: 'api-key',
        q: 'How do I get a RareMargin API key for an AI assistant?',
        a: 'Assistants that sign in don’t need one. For Cursor, Codex or a script, an admin creates a key in RareMargin under Settings, API & MCP, and picks its role: a Viewer key only reads, and an Accountant key can also draft entries and accruals for approval.',
        more: [
          'The full key, which starts with ck_, is shown once, so store it in a password or secret manager. Name the key after the assistant, such as “Claude Code”. An Admin key has full access, which an assistant doesn’t need.',
        ],
      },
      {
        id: 'claude-code',
        q: 'How do I connect Claude Code to RareMargin?',
        a: 'Run one command, then open /mcp in Claude Code, pick raremargin and sign in to RareMargin in the browser. Add --scope user to use it in every project.',
        more: [
          { code: `claude mcp add --transport http raremargin ${f.origin}/mcp` },
          'Or skip the sign-in with an API key in a header:',
          { code: `claude mcp add --transport http raremargin ${f.origin}/mcp \\\n  --header "Authorization: Bearer $RAREMARGIN_API_KEY"` },
        ],
      },
      {
        id: 'claude-desktop',
        q: 'How do I connect Claude Desktop to RareMargin?',
        a: `Add RareMargin as a custom connector under Settings, Connectors, Add custom connector, with the URL ${f.origin}/mcp, and sign in to RareMargin. A connector added on claude.ai shows up in Claude Desktop as well.`,
      },
      {
        id: 'cursor',
        q: 'How do I connect Cursor to RareMargin?',
        a: 'Add RareMargin to Cursor’s mcp.json, in ~/.cursor for every project or in a project’s .cursor folder, with the API key in a header.',
        more: [
          { code: JSON.stringify({ mcpServers: { raremargin: { url: `${f.origin}/mcp`, headers: { Authorization: 'Bearer ${env:RAREMARGIN_API_KEY}' } } } }, null, 2) },
          'Cursor fills in ${env:RAREMARGIN_API_KEY} from your environment, so the key stays out of the file.',
        ],
      },
      {
        id: 'codex',
        q: 'How do I connect OpenAI’s Codex to RareMargin?',
        a: 'Add RareMargin to ~/.codex/config.toml, and Codex sends the API key from an environment variable as a Bearer token.',
        more: [{ code: `[mcp_servers.raremargin]\nurl = "${f.origin}/mcp"\nbearer_token_env_var = "RAREMARGIN_API_KEY"` }],
      },
      {
        id: 'safety',
        q: 'Is it safe to let an AI assistant into my books?',
        a: 'An assistant can do only what the role you gave it allows, and RareMargin’s two write tools create approval requests, never postings, so a person decides what reaches the ledger.',
        more: [
          'Assistants and API keys can’t approve anything. Every proposal made over MCP waits for a person, whatever approval rules the company has set, and records that it came over MCP and which app sent it. Disconnecting the app or revoking the key in Settings cuts the assistant off at once.',
        ],
      },
    ],
    sources: [
      { label: 'Model Context Protocol', url: 'https://modelcontextprotocol.io/' },
      { label: 'Claude Code: connect to MCP servers', url: 'https://code.claude.com/docs/en/mcp' },
      { label: 'Claude: authentication for connectors', url: 'https://claude.com/docs/connectors/building/authentication' },
      { label: 'Cursor: Model Context Protocol', url: 'https://cursor.com/docs/context/mcp' },
      { label: 'Codex: MCP', url: 'https://developers.openai.com/codex/mcp' },
      { label: 'MCP: authorization', url: 'https://modelcontextprotocol.io/specification/2025-11-25/basic/authorization' },
    ],
    related: ['/docs/api-mcp-server-and-webhooks', '/docs/ai-journal-entries-and-approvals', '/pricing', '/about'],
  }),

  '/docs/set-up-raremargin-and-move-your-books': (f, price) => ({
    eyebrow: 'Guide',
    h1: 'Set up RareMargin and move your books in',
    lead: `You can sign up for RareMargin and use it the same day: a new company starts with a 42-account chart of accounts, or you can explore a sample SaaS company first. Moving your books in takes longer, because RareMargin has no one-click import yet. This guide covers each step, the bank CSV format, who to invite and when to subscribe.`,
    facts: {
      head: ['Step', 'What it takes'],
      rows: [
        ['Sign up', `Your name, email, a password, the company’s name and its currency; no card for the ${f.trialDays}-day trial`],
        ['Chart of accounts', '42 accounts to start; add the ones your books use'],
        ['Opening balances', 'One journal entry, from your old system’s trial balance'],
        ['Customers and revenue', 'Products, customers and contracts; contracts schedule the invoices and the revenue'],
        ['Vendors and bills', 'Vendors, then the bills you receive from your start date'],
        ['Bank', 'Each bank account, tied to its cash account, and a CSV statement or the API'],
        ['Team', 'At least one other person, so someone else approves what you submit'],
      ],
    },
    sections: [
      {
        id: 'how-long',
        q: 'How long does it take to set up RareMargin?',
        a: 'You can sign up and use RareMargin the same day; moving a company’s books in takes longer, because RareMargin has no one-click import yet.',
        more: [
          'Sign-up creates the company straight away, with a 42-account chart of accounts and a first entity in the currency you choose. A sample company builds in the background while a loading page shows. How long the move takes depends on how many accounts, customers, contracts and vendors you bring, and on whether you script it against the API.',
        ],
      },
      {
        id: 'sign-up',
        q: 'What do I need to sign up for RareMargin?',
        a: `Your name, an email address, a password of at least 10 characters, the company’s name and its base currency. RareMargin needs no card for the ${f.trialDays}-day trial.`,
        more: [
          'RareMargin supports 22 currencies, including USD, EUR, GBP, INR, CAD, AUD and SGD. You confirm your email address from a link RareMargin sends, and you can turn on two-factor sign-in with an authenticator app at any time.',
        ],
      },
      {
        id: 'sample',
        q: 'Should I start on a sample company or on my own books?',
        a: 'Start on the sample company to see RareMargin working with real-shaped data, or on your own books when you are ready to move in; you choose when you sign up.',
        more: [
          `The sample is a SaaS company with 14 months of history: contracts, invoices, bills, bank lines, two entities (US and UK) and a closed ledger. Every company, sample or not, gets the same ${f.trialDays}-day trial.`,
        ],
      },
      {
        id: 'move',
        q: 'How do I move my books from QuickBooks or spreadsheets into RareMargin?',
        a: 'Add the accounts you use, post opening balances as one journal entry, add products, customers, contracts and vendors, then import bank lines from CSV; run both systems side by side for a month before you switch.',
        more: [
          {
            list: [
              'Chart of accounts: start from the 42 accounts RareMargin creates and add the ones your books use.',
              'Opening balances: post one journal entry dated the day before your start date, with the balances from your old system’s trial balance on that day.',
              'Customers and revenue: add your products, which say how their revenue is recognized, then each customer and contract. RareMargin schedules the invoices and the revenue from the contract.',
              'Vendors and bills: add your vendors, and enter bills as they arrive from your start date. Approved bills post to payables and expense.',
              'Bank: add each bank account, tie it to its cash account in the ledger, and upload a CSV statement. Every line gets a suggested match.',
              'Check: compare RareMargin’s trial balance with the old system’s for each month you run both.',
            ],
          },
          'Most of this can be scripted against RareMargin’s REST API, which creates accounts, journal entries, customers, contracts, vendors and bills.',
        ],
      },
      {
        id: 'csv',
        q: 'What format does a bank CSV need for RareMargin?',
        a: 'A header row, then one row per transaction with a date, a description, and either one signed amount or separate debit and credit columns. RareMargin recognizes the column names most banks use.',
        more: [
          {
            table: {
              head: ['Column', 'Header names RareMargin recognizes'],
              rows: [
                ['Date (required)', 'date, posted date, transaction date, posting date'],
                ['Description (required)', 'description, memo, details, narrative'],
                ['Amount', 'amount or value, positive for money in and negative for money out'],
                ['Or debit and credit', 'debit, withdrawal or money out; credit, deposit or money in'],
                ['Counterparty (optional)', 'counterparty, payee, merchant, name'],
                ['Reference (optional)', 'reference, ref, check number, id'],
              ],
            },
          },
          'Dates can be YYYY-MM-DD or MM/DD/YYYY. Dollar, pound, euro and rupee signs and thousands separators are ignored, an amount in parentheses counts as negative, and a row with a zero amount stops the import with its row number.',
        ],
      },
      {
        id: 'integrations',
        q: 'Does RareMargin connect to my bank, Stripe or QuickBooks?',
        a: 'Not directly yet. RareMargin has no bank connections and no native integrations, so bank lines come in by CSV or the API, and other tools connect through the REST API, webhooks and the MCP server.',
        more: [
          'A developer can sync billing or bank data through the REST API at /v1, whose writes are idempotent, and follow changes with signed webhooks for 34 event types. AI assistants such as Claude and ChatGPT connect through the MCP server.',
        ],
      },
      {
        id: 'team',
        q: 'Who should I invite to RareMargin, and with which role?',
        a: 'Invite at least one other person, because RareMargin’s approval rules keep anyone from approving their own entry. Each person gets one of four roles: owner, admin, accountant or viewer.',
        more: ['Owners and admins invite people under Settings, Members, and create API keys. Viewers only read. Only the owner can delete the company.'],
      },
      {
        id: 'subscribe',
        q: 'When do I need to subscribe to RareMargin?',
        a: `Before the ${f.trialDays}-day trial ends, if you want to keep working in the books: an owner or admin subscribes from Settings, Billing, and days left on the trial carry over.`,
        more: [
          `RareMargin costs ${per(price)}, with every feature and no per-user fee. If nobody subscribes, the company becomes read-only: nothing is deleted, and reports and CSV downloads keep working.`,
        ],
      },
    ],
    related: ['/compare/raremargin-vs-quickbooks-online', '/docs/api-mcp-server-and-webhooks', '/docs/how-continuous-close-works', '/pricing'],
  }),

  '/about': (f, price) => {
    const maker = f.legalName ? `${f.legalName}${f.address ? `, ${f.address}` : ''}` : null;
    return {
      eyebrow: 'About',
      h1: 'About RareMargin',
      lead: `RareMargin is an AI-native general ledger with continuous close for finance teams at SaaS companies.${maker ? ` It is made and sold by ${maker}.` : ''}`,
      facts: {
        rows: [
          ['Product', 'General ledger with continuous close: revenue and AR, spend and AP, cash, controls and reports'],
          ['For', 'Finance teams at SaaS companies'],
          ['Price', `${cap(per(price))}, every feature included`],
          ['Free trial', `${f.trialDays} days, no card needed`],
          ['Made by', maker ?? 'Named on the contact page'],
          ['Contact', f.email],
          ['Payments', 'Dodo Payments, merchant of record'],
          ['Runs on', 'Cloudflare, with the database on Neon in AWS US East'],
        ],
      },
      sections: [
        {
          id: 'what',
          q: 'What is RareMargin?',
          a: 'RareMargin is an AI-native general ledger with continuous close for finance teams at SaaS companies.',
          more: [
            'Revenue schedules, prepaid amortization, accruals and bank matching post to one double-entry ledger as they happen. An AI assistant drafts the entries you would otherwise write by hand, and nothing it drafts reaches the books until a person approves it.',
          ],
        },
        {
          id: 'who',
          q: 'Who makes RareMargin?',
          a: maker ? `RareMargin is made and sold by ${maker}. You can reach the team at ${f.email}.` : `RareMargin’s operator is named on the contact page, and you can reach the team at ${f.email}.`,
          more: ['Subscriptions are sold through Dodo Payments, the merchant of record, which takes the payment, issues the invoice and handles sales tax or VAT.'],
        },
        {
          id: 'where',
          q: 'Where does RareMargin run, and who processes data?',
          a: 'RareMargin runs on Cloudflare, keeps the books in a Neon database on AWS in the US East region, and uses OpenAI only for the assistant.',
          more: [
            {
              table: {
                head: ['Provider', 'What it does'],
                rows: [
                  ['Cloudflare', 'Hosting, protection from attacks, and email'],
                  ['Neon (Databricks)', 'The database and its backups, on AWS US East'],
                  ['OpenAI', 'The model behind the assistant and AI-built dashboards; not used for training'],
                  ['Google (Gmail)', 'The inbox for support and billing email'],
                  ['Dodo Payments', 'Checkout, payment, invoices, tax and refunds'],
                ],
              },
            },
            'The privacy policy and the data processing terms list what each one receives.',
          ],
        },
        {
          id: 'trust',
          q: 'How does RareMargin keep the books trustworthy?',
          a: 'Every entry must balance, every posted entry is sealed in a SHA-256 hash chain, 11 controls run continuously, and nobody can approve their own entry.',
          more: ['People sign in with a password and, optionally, an authenticator app, and each has one of four roles. API keys carry a role too, and AI agents can only propose.'],
        },
        {
          id: 'limits',
          q: 'What doesn’t RareMargin do yet?',
          a: 'RareMargin has no SOC 2 report, no single sign-on, no direct bank connections and no native integrations with billing or CRM tools yet.',
          more: [
            {
              list: [
                'Bank transactions come in by CSV upload or through the API.',
                'Other systems connect through the REST API, webhooks and the MCP server.',
                'It is a finance system, not a full ERP: there is no inventory, order management or payroll.',
              ],
            },
            `If one of these matters to you, write to ${f.email}; it helps decide what comes next.`,
          ],
        },
        {
          id: 'cost',
          q: 'How much does RareMargin cost?',
          a: `${cap(per(price))}, with every feature included, after a ${f.trialDays}-day free trial that needs no card. You can cancel any time, and you can get a full refund within 14 days of your first payment.`,
        },
      ],
      related: ['/pricing', '/compare/raremargin-vs-rillet', '/docs/how-continuous-close-works', '/contact'],
    };
  },
};

/** One article as Markdown, for llms-full.txt and for agents that ask a page for text/markdown. */
export function articleMarkdown(path: ArticlePath, f: Facts, price: string | null) {
  const a = ARTICLES[path](f, price);
  const meta = ARTICLE_META[path];
  const table = (t: Table) => {
    const head = t.head ?? t.rows[0]!.map(() => '');
    const row = (r: string[]) => `| ${r.map((c) => c.replace(/\|/g, '\\|')).join(' | ')} |`;
    return [row(head), row(head.map(() => '---')), ...t.rows.map(row)].join('\n');
  };
  const block = (b: Block) => (typeof b === 'string' ? b : 'list' in b ? b.list.map((x) => `- ${x}`).join('\n') : 'table' in b ? table(b.table) : `\`\`\`\n${b.code}\n\`\`\``);
  return [
    `# ${a.h1}`,
    `${f.origin}${path} · Updated ${meta.updated}`,
    a.lead,
    ...(compareNote(path) ? [`*${compareNote(path)}*`] : []),
    ...(a.facts ? [table(a.facts)] : []),
    ...a.sections.flatMap((s) => [`## ${s.q}`, s.a, ...(s.more ?? []).map(block)]),
    ...(a.sources ? [`Sources${meta.kind === 'compare' ? `, read ${CHECKED}` : ''}:\n${a.sources.map((s) => `- [${s.label}](${s.url})`).join('\n')}`] : []),
  ].join('\n\n');
}
